import { c as _c } from "react/compiler-runtime";
import type { BiometricRegisterChallengeJson, BiometricRegisterRequest } from '@onefootprint/types';
import { AuthMethodKind } from '@onefootprint/types';
import { useMutation } from '@tanstack/react-query';
import base64url from 'base64url';
import { useUserChallenge, useUserChallengeVerify } from '../../../components/identify/queries';
const toUint8Array = (buffer: Buffer): Uint8Array<ArrayBuffer> => {
  const arrayBuffer = new ArrayBuffer(buffer.length);
  const uint8Array = new Uint8Array(arrayBuffer);
  uint8Array.set(buffer);
  return uint8Array;
};
const registerPasskeyOnDevice = async (challenge: string) => {
  const challengeJson = JSON.parse(challenge) as BiometricRegisterChallengeJson;
  const {
    publicKey
  } = challengeJson;
  publicKey.challenge = toUint8Array(base64url.toBuffer(publicKey.challenge as unknown as string));
  publicKey.user.id = toUint8Array(base64url.toBuffer(publicKey.user.id as unknown as string));
  const publicKeyCredential = (await window.navigator.credentials.create({
    publicKey
  })) as PublicKeyCredential;
  const attestationObject = base64url.encode(
  // @ts-expect-error: fix-me Property 'attestationObject' does not exist on type 'AuthenticatorResponse'....
  publicKeyCredential.response.attestationObject as unknown as Buffer);
  const clientDataJSON = base64url.encode(publicKeyCredential.response.clientDataJSON as unknown as Buffer);
  const pk = {
    rawId: base64url.encode(publicKeyCredential.rawId as unknown as Buffer),
    id: publicKeyCredential.id,
    type: 'public-key',
    response: {
      clientDataJSON,
      attestationObject
    }
  };
  return JSON.stringify(pk);
};

/// A special kind of error returned when we fail to register a passkey in the OS prompt
type RegisterPasskeyError = {
  kind: 'registerPasskeyError';
  elapsedTimeInOsPromptMs: number;
  error: unknown;
};
export const isRegisterPasskeyError = (e: unknown): e is RegisterPasskeyError => (e as RegisterPasskeyError)?.kind === 'registerPasskeyError';
const useBiometricInit = () => {
  const $ = _c(3);
  const userChallengeMut = useUserChallenge();
  const userChallengeVerifyMut = useUserChallengeVerify();
  let t0;
  if ($[0] !== userChallengeMut || $[1] !== userChallengeVerifyMut) {
    t0 = {
      mutationFn: async t1 => {
        const {
          authToken,
          actionKind
        } = t1;
        const {
          challengeToken,
          biometricChallengeJson
        } = await userChallengeMut.mutateAsync({
          authToken,
          kind: AuthMethodKind.passkey,
          actionKind
        });
        if (!biometricChallengeJson) {
          throw new Error("No biometric challenge JSON when registering passkey");
        }
        const startPasskeyRegister = new Date();
        let challengeResponse;
        ;
        try {
          challengeResponse = await registerPasskeyOnDevice(biometricChallengeJson);
        } catch (t2) {
          const error = t2;
          const endPasskeyRegister = new Date();
          const elapsedTimeInOsPromptMs = endPasskeyRegister.getTime() - startPasskeyRegister.getTime();
          const e = {
            kind: "registerPasskeyError",
            elapsedTimeInOsPromptMs,
            error
          };
          return Promise.reject(e);
        }
        const response = await userChallengeVerifyMut.mutateAsync({
          authToken,
          challengeToken,
          challengeResponse
        });
        return {
          response,
          deviceResponseJson: challengeResponse
        };
      }
    };
    $[0] = userChallengeMut;
    $[1] = userChallengeVerifyMut;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
};
export default useBiometricInit;